import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { api } from '../lib/api';
import { API_BASE } from '../lib/config';

const EmailAuthPage = () => {
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, getDashboardUrl } = useAuth();

  // URLからuser_typeを取得（クエリパラメータから）
  const searchParams = new URLSearchParams(location.search);
  const userType = searchParams.get('type') || 'job_seeker';

  // ローカル環境判定（0.0.0.0 や LAN IP でも true）
  const isLocal = typeof window !== 'undefined' && (() => {
    const host = window.location.hostname || '';
    if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') return true;
    // 10.x.x.x / 192.168.x.x / 172.16-31.x.x をローカル扱い
    if (/^10\./.test(host)) return true;
    if (/^192\.168\./.test(host)) return true;
    const m = host.match(/^172\.(\d{1,2})\./);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n >= 16 && n <= 31) return true;
    }
    return false;
  })();

  // 既にログインしているユーザーが不適切なタイプの登録を試みる場合のチェック
  useEffect(() => {
    if (user) {
      const isConflictingType =
        (user.user_type === 'job_seeker' && userType === 'employer') ||
        (user.user_type === 'employer' && userType === 'job_seeker');

      if (isConflictingType) {
        const currentUserTypeText = user.user_type === 'job_seeker' ? '求職者' : '事業者';
        const attemptingTypeText = userType === 'job_seeker' ? '求職者' : '事業者';

        toast.error(`${currentUserTypeText}としてログイン中のため、${attemptingTypeText}登録はできません。`);
        navigate('/'); // ホームページにリダイレクト
        return;
      }
    }
  }, [user, userType, navigate]);


  // Social Login Handler
  const handleSocialLogin = async (provider) => {
    try {
      setLoading(true);
      // Pass the userType to the backend to maintain context (job_seeker or employer)
      // Also pass the current window origin as front_url to ensure the backend generates correct redirect_uri
      const frontUrl = window.location.origin;
      const res = await fetch(`${API_BASE}/auth/${provider}/url?user_type=${userType}&front_url=${encodeURIComponent(frontUrl)}`);
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('ログインURLの取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      toast.error('エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const getUserTypeName = () => {
    return userType === 'job_seeker' ? '求職者' : '事業者';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <EnvelopeIcon className="mx-auto h-12 w-12 text-blue-600" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {getUserTypeName()}アカウント作成
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div>
              {/* Account Type Selection Link */}
              <div className="mb-6 p-1 bg-blue-50 border border-blue-100 rounded-lg text-center">
                <p className="text-sm text-blue-800 mb-2">
                  現在は<span className="font-bold">{getUserTypeName()}</span>として登録中です
                </p>
                <Link
                  to="/register/type"
                  className="text-xs text-blue-600 hover:text-blue-800 underline font-medium focus:outline-none"
                >
                  アカウントタイプを選び直す
                </Link>
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  アカウント作成
                </h2>
                <p className="text-sm text-gray-600">
                  SNSアカウントで登録できます。
                </p>
              </div>

              {/* Social Login Buttons */}
              <div className="mb-8 space-y-3">
                <button
                  type="button"
                  onClick={() => handleSocialLogin('google')}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 shadow-sm text-sm font-bold rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  Googleで登録（30秒で完了）
                </button>
                <button
                  type="button"
                  onClick={() => handleSocialLogin('line')}
                  className="w-full flex items-center justify-center px-4 py-3 border border-transparent shadow-sm text-sm font-bold rounded-md text-white bg-[#00B900] hover:bg-[#009900] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00B900] transition-colors"
                >
                  <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.2 6.8c.2 1.3.2 2.6.2 3.9 0 5.5-4.9 10.3-10.9 9.3C4 19.1.5 14.8.5 9.8c0-5 4.5-9 9.9-9 4.3-.1 8.5 2.6 9.8 6z" />
                    <path d="M12 1C5.9 1 1 5.4 1 10.8c0 5 4.2 9.1 9.8 9.6 1.1.1 1.3-.8 1.2-1.4v-2.2c0-.5 0-.5 1-1 4.5-.4 7.6-3.8 7.6-7.8C20.6 4.3 16.5 1 12 1zm5.6 11.6h-3.6c-.3 0-.5-.2-.5-.5v-4c0-.3.2-.5.5-.5.3 0 .5.2.5.5v3.5h3.1c.3 0 .5.2.5.5 0 .3-.2.5-.5.5zm-5.4 0c0 .3-.2.5-.5.5h-.1c-.1 0-.3-.1-.4-.2l-2.4-3.3v3c0 .3-.2.5-.5.5h-.1c-.3 0-.5-.2-.5-.5v-4c0-.3.2-.5.5-.5.1 0 .3.1.4.2l2.4 3.3v-3c0-.3.2-.5.5-.5s.5.2.5.5v4zm-5.4 0H3.7c-.3 0-.5-.2-.5-.5v-4c0-.3.2-.5.5-.5s.5.2.5.5v4c0 .3-.2.5.5.5z" stroke="white" strokeWidth="1.5" />
                  </svg>
                  LINEで登録（30秒で完了）
                </button>
              </div>
            </div>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    すでにアカウントをお持ちですか？
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  ログイン
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailAuthPage;